<template>
  <v-dialog v-model="dialogOpen" persistent width="80%" :fullscreen="fullscreen">
    <template v-slot:activator="{ on: dialogOn }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltipOn }">
          <v-btn v-on="{ ...dialogOn, ...tooltipOn }" :small="smallButton" style="position:absolute;top:0;right:0;z-index:990;">
            <v-icon :small="smallButton">fal fa-pencil</v-icon>
          </v-btn>
        </template>
        <span>Edit this block</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-toolbar>
        <v-toolbar-title>Block Editor</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tooltip :top="!fullscreen" :bottom="fullscreen">
          <template v-slot:activator="{ on }">
            <v-btn icon @click="fullscreen = !fullscreen">
              <v-icon small v-if="fullscreen">far fa-compress-arrows-alt</v-icon>
              <v-icon small v-else>far fa-expand-arrows-alt</v-icon>
            </v-btn>
          </template>
          <span v-if="fullscreen">Exit full-screen mode</span>
          <span v-else>Expand to full-screen mode</span>
        </v-tooltip>
        <v-menu bottom>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" text>
              {{ getLabel(type) }}
              <v-icon right small>fas fa-caret-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-for="({ label, code }) in blockTypes" :key="'type-' + code" @click="type = code">
              <v-list-item-title>{{ label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>
      <component :is="component" :value="editingItem" ref="subComponent" @update="update"></component>
      <v-card-actions>
        <v-btn text @click="dialogOpen = false">Close</v-btn>
        <v-btn :disabled="isSaving" text color="success" @click="save">{{ isSaving ? 'Saving...' : 'Save Changes' }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="!isFirst" text @click="$emit('moveUp')">
          <v-icon left>fal fa-arrow-up</v-icon>
          Move Up
        </v-btn>
        <v-btn v-if="!isLast" text @click="$emit('moveDown')">
          <v-icon left>fal fa-arrow-down</v-icon>
          Move Down
        </v-btn>
        <v-btn v-if="inPrimary" text @click="$emit('moveRight')">
          <v-icon left>fal fa-arrow-right</v-icon>
          Move Right
        </v-btn>
        <v-btn v-else text @click="$emit('moveLeft')">
          <v-icon left>fal fa-arrow-left</v-icon>
          Move Left
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text color="error" @click="$emit('remove')">Remove Block</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from '@vue/composition-api'
export default {
  props: {
    id: {
      type: String,
      required: true
    },
    isFirst: {
      type: Boolean,
      default: true
    },
    isLast: {
      type: Boolean,
      default: true
    },
    inPrimary: {
      type: Boolean,
      default: true
    },
    smallButton: {
      type: Boolean,
      default: false
    }
  },
  components: {
    TextBlockSettings: () => import('@/components/admin/editor/text'),
    ImageBlockSettings: () => import('@/components/admin/editor/image'),
    VideoBlockSettings: () => import('@/components/admin/editor/video'),
    QuizBlockSettings: () => import('@/components/admin/editor/quiz'),
    CarouselBlockSettings: () => import('@/components/admin/editor/carousel'),
    AgreementSettings: () => import('@/components/admin/editor/agreement'),
    PopupSettings: () => import('@/components/admin/editor/popups'),
    TimelineSettings: () => import('@/components/admin/editor/timeline')
  },
  setup (props, { root, emit }) {
    const dialogOpen = ref(false)
    const fullscreen = ref(false)
    const type = ref('')
    const editingItem = reactive({
      type: ''
    })
    const blockTypes = ref([
      { label: 'Text Card', code: 'TextCard' },
      { label: 'Image Card', code: 'ImageCard' },
      { label: 'Video Card', code: 'VideoCard' },
      { label: 'Quiz Card', code: 'QuizCard' },
      { label: 'Carousel', code: 'Carousel' },
      { label: 'Agreement', code: 'Agreement' },
      { label: 'Popup Set', code: 'Popups' },
      { label: 'Timeline', code: 'Timeline' }
    ])
    const component = computed(() => {
      switch (type.value) {
        case 'TextCard':
          return 'text-block-settings'
        case 'ImageCard':
          return 'image-block-settings'
        case 'VideoCard':
          return 'video-block-settings'
        case 'QuizCard':
          return 'quiz-block-settings'
        case 'Carousel':
          return 'carousel-block-settings'
        case 'Agreement':
          return 'agreement-settings'
        case 'Popups':
          return 'popup-settings'
        case 'Timeline':
          return 'timeline-settings'
      }
    })
    const subComponent = ref(null)

    const onPatchedListener = (data) => { if (data._id === props.id) loadBlock() }

    onMounted(() => { root.$feathers.service('orientation/block').on('patched', onPatchedListener) })
    onBeforeUnmount(() => { root.$feathers.service('orientation/block').removeListener('patched', onPatchedListener) })

    function loadBlock () {
      root.$feathers.service('orientation/block').get(props.id).then((data) => {
        type.value = data.type
        for (let l in data) {
          editingItem[l] = data[l]
        }
      })
    }
    loadBlock()

    function getLabel (code) {
      for (let i = 0; i < blockTypes.value.length; i++) {
        if (blockTypes.value[i].code === code) return blockTypes.value[i].label
      }
      return 'Invalid Block Type'
    }

    function update (item) {
      for (let l in item) editingItem[l] = item[l]
    }

    const isSaving = ref(false)
    function save () {
      if (subComponent.value.validate()) {
        isSaving.value = true
        editingItem.type = type.value
        root.$feathers.service('orientation/block').patch(props.id, editingItem).then((data) => {
          emit('change', data)
          isSaving.value = false
          dialogOpen.value = false
        })
      }
    }

    return {
      dialogOpen,
      fullscreen,
      type,
      editingItem,
      blockTypes,
      component,
      subComponent,
      getLabel,
      update,
      isSaving,
      save
    }
  }
}
</script>
